import * as React from "react";
import NextApp from "next/app";
import getConfig from "next/config";
import { getToken } from "@grandvisionhq/auth";
import packageInfo from "@grandvisionhq/auth/package.json";
import { initApolloClient, ApolloProvider } from "@grandvisionhq/graphql";
const CORE_VERSION = packageInfo.dependencies["@grandvisionhq/common"];
const { publicRuntimeConfig } = getConfig();
const { grandvision } = publicRuntimeConfig;
const graphQLUrl = (() => {
  if (typeof window === "undefined" && process.env.GRAPHQL_ENDPOINT) {
    const gqlEndpoint = process.env.GRAPHQL_ENDPOINT;
    try {
      return new URL(gqlEndpoint).toString();
    } catch (error) {
      console.warn(
        `Tried to use internal GQL endpoint "${gqlEndpoint}" but it seems to be invalid. Will use the default external URL instead`,
        error
      );
    }
  }
  return grandvision.endpoints.graphql;
})();
const clientInfo = publicRuntimeConfig.apolloClientInfo || {};
const recaptchaConfig = publicRuntimeConfig.recaptcha || {};
const getIsPersistentQueryDisabled = () => {
  const { features = [] } = getConfig().publicRuntimeConfig.grandvision;
  return features.includes("PERSISTENT_QUERY_DISABLED");
};
const getDisplayName = (Component) => Component.displayName || Component.name || "Unknown";
const withApollo = () => (PageComponent) => {
  const WithApollo = ({
    apolloClient,
    apolloState,
    recaptchaContext,
    ...pageProps
  }) => {
    const [isMounted, setIsMounted] = React.useState(false);
    React.useEffect(() => {
      setIsMounted(true);
    }, []);
    const versions = {
      ...clientInfo.versions,
      core: CORE_VERSION
    };
    const isClient = typeof window !== "undefined" && isMounted;
    const isRecaptchaReady = recaptchaContext?.isReady || false;
    const recaptchaExecute = isClient && isRecaptchaReady && recaptchaContext?.execute ? recaptchaContext.execute : void 0;
    const client = React.useMemo(() => {
      if (apolloClient && (!isClient || !recaptchaExecute)) {
        return apolloClient;
      }
      return initApolloClient(graphQLUrl, {
        initialState: apolloState,
        getToken,
        queryOptions: { isPersistentQueryDisabled: getIsPersistentQueryDisabled() },
        clientInfo: {
          ...clientInfo,
          versions
        },
        getRecaptchaToken: recaptchaExecute ? async (action) => {
          const result = await recaptchaExecute({ action });
          return result.token;
        } : void 0,
        recaptcha: {
          protectedOperations: recaptchaConfig.protectedOperations ? new Set(recaptchaConfig.protectedOperations) : /* @__PURE__ */ new Set([])
        }
      });
    }, [apolloClient, apolloState, recaptchaExecute, isClient, isRecaptchaReady]);
    return /* @__PURE__ */ React.createElement(ApolloProvider, { client }, /* @__PURE__ */ React.createElement(PageComponent, { ...pageProps }));
  };
  if (PageComponent.getInitialProps) {
    WithApollo.getInitialProps = async (ctx) => {
      const inAppContext = Boolean(ctx?.ctx);
      if (ctx.apolloClient) {
        console.error("Multiple instances of withApollo found.");
        throw new Error("Multiple instances of withApollo found.");
      }
      const apolloClient = initApolloClient(graphQLUrl, {
        getToken,
        queryOptions: { isPersistentQueryDisabled: getIsPersistentQueryDisabled() }
      });
      ctx.apolloClient = apolloClient;
      if (inAppContext) {
        ctx.ctx.apolloClient = apolloClient;
      }
      let pageProps = {};
      if (PageComponent.getInitialProps) {
        pageProps = await PageComponent.getInitialProps(ctx);
      } else if (inAppContext) {
        pageProps = await NextApp.getInitialProps(ctx);
      }
      if (typeof window === "undefined") {
        if (ctx.res && (ctx.res.headersSent || ctx.res.finished)) {
          return pageProps;
        }
      }
      const apolloState = apolloClient.cache.extract();
      return {
        ...pageProps,
        apolloState
      };
    };
  }
  WithApollo.displayName = `WithApollo(${getDisplayName(PageComponent)})`;
  return WithApollo;
};
export {
  withApollo as default
};
//# sourceMappingURL=with-apollo.js.map
